import { css, SerializedStyles } from '@emotion/core';
import styled from '@emotion/styled';
import { Button, Theme } from '@material-ui/core';
import { ICustomTheme } from 'interfaces';

export const setColor = (
  variantColor: string,
  theme: ICustomTheme,
): SerializedStyles => {
  switch (variantColor) {
    case 'primary':
      return css`
        background-color: ${theme.colors.paletteFromBO.main};
        color: ${theme.colors.white};
        &:hover {
          background-color: ${theme.colors.white};
          color: ${theme.colors.paletteFromBO.main};
          border: 2px solid ${theme.colors.paletteFromBO.main};
        }
      `;
    case 'primaryInverted':
      return css`
        background-color: ${theme.colors.white};
        color: ${theme.colors.paletteFromBO.main};
        border: 2px solid ${theme.colors.paletteFromBO.main};
        &:hover {
          background-color: ${theme.colors.paletteFromBO.main};
          color: ${theme.colors.white};
          border: none;
        }
      `;
    case 'secondary':
      return css`
        background-color: ${theme.colors.paletteFromBO.primary};
        color: ${theme.colors.white};
        &:hover {
          background-color: ${theme.colors.white};
          color: ${theme.colors.paletteFromBO.primary};
          border: 2px solid ${theme.colors.paletteFromBO.primary};
        }
      `;
    case 'secondaryInverted':
      return css`
        background-color: ${theme.colors.white};
        color: ${theme.colors.paletteFromBO.primary};
        border: 2px solid ${theme.colors.paletteFromBO.primary};
        &:hover {
          background-color: ${theme.colors.paletteFromBO.primary};
          color: ${theme.colors.white};
          border: none;
        }
      `;
    case 'loading':
    case 'info':
      return css`
        background-color: ${theme.colors.grey.light};
        color: ${theme.colors.white};
        &:hover {
          background-color: ${theme.colors.white};
          color: ${theme.colors.grey.light};
          border: 2px solid ${theme.colors.grey.light};
        }
      `;
    case 'infoInverted':
      return css`
        background-color: ${theme.colors.white};
        border: 2px solid ${theme.colors.grey.light};
        color: ${theme.colors.grey.light};
        &:hover {
          background-color: ${theme.colors.grey.light};
          color: ${theme.colors.white};
          border: none;
        }
      `;
    case 'disabled':
      return css`
        background-color: ${theme.colors.paletteFromBO.main};
        color: ${theme.colors.white} !important;
        opacity: 0.5;
        &:hover {
          background-color: ${theme.colors.white};
          color: ${theme.colors.paletteFromBO.main};
          border: 2px solid ${theme.colors.paletteFromBO.main};
        }
      `;
    case 'success':
      return css`
        background-color: ${theme.colors.success.main};
        color: ${theme.colors.white};
        &:hover {
          background-color: ${theme.colors.white};
          color: ${theme.colors.success.main};
          border: 2px solid ${theme.colors.success.main};
        }
      `;
    case 'error':
      return css`
        background-color: ${theme.colors.error.main};
        color: ${theme.colors.white};
        &:hover {
          background-color: ${theme.colors.white};
          color: ${theme.colors.error.main};
          border: 2px solid ${theme.colors.error.main};
        }
      `;
    default:
      return css`
        background-color: ${theme.colors.paletteFromBO.main};
        color: ${theme.colors.white};
      `;
  }
};

export const setSize = (
  variantSize: string,
  theme: Theme,
): SerializedStyles => {
  switch (variantSize) {
    case 'sm':
      return css`
        font-size: 1.2rem;
        height: ${theme.spacing(4)}px;
        margin: ${theme.spacing(0, 1)};
        width: ${theme.spacing(18)}px;
      `;
    case 'md':
      return css`
        font-size: 1.6rem;
        height: ${theme.spacing(6)}px;
        margin: ${theme.spacing(0, 3)};
        width: ${theme.spacing(24)}px;
      `;
    case 'lg':
      return css`
        font-size: 2rem;
        height: ${theme.spacing(9)}px;
        margin: ${theme.spacing(0, 3)};
        width: ${theme.spacing(34)}px;
      `;
    case 'xl':
      return css`
        font-size: 3rem;
        height: ${theme.spacing(11)}px;
        margin: 0 ${theme.spacing(3)}px;
        width: 400px;
      `;
    default:
      return css`
        font-size: 1.8rem;
        height: ${theme.spacing(7)}px;
        margin: ${theme.spacing(0, 4)};
        width: ${theme.spacing(34)}px;
      `;
  }
};

export const StyledButton = styled(Button)<{
  boxShadow: boolean;
  textBold: boolean;
  variantColor: string;
  variantSize: string;
  theme: ICustomTheme;
}>`
  border: none;
  border-radius: ${({ theme }) => `${theme.spacing(6.5)}px`};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  line-height: ${({ theme }) => `${theme.typography.h1.lineHeight}`};
  margin-left: ${({ theme }) => `${theme.spacing(0.5)}px`};
  margin-right: ${({ theme }) => `${theme.spacing(0.5)}px`};
  min-width: ${({ theme }) => `${theme.spacing(7)}px`};
  padding-left: ${({ theme }) => `${theme.spacing(2.5)}px`};
  padding-right: ${({ theme }) => `${theme.spacing(2.5)}px`};
  text-align: center;
  text-transform: none;
  font-weight: ${(props) => (props.textBold ? 'bold' : 'normal')};
  box-shadow: ${(props) =>
    props.boxShadow ? '0 3px 6px 0 rgba(0, 0, 0, 0.16)' : 'unset'};
  ${({ variantColor, theme }) => setColor(variantColor, theme)};
  ${({ variantSize, theme }) => setSize(variantSize, theme)};
  svg {
    margin-right: ${({ theme }) => `${theme.spacing(0.5)}px`};
  }
`;

export const setButtonSize = (
  variantSize: string,
  theme: ICustomTheme,
  isMobile: boolean,
) => {
  switch (variantSize) {
    case 'sm':
      return `
        font-size: 1.2rem;
        height: ${theme.spacing(4)}px;
        margin: ${theme.spacing(0, 1)};
        width: ${theme.spacing(18)}px;
      `;
    case 'md':
      return `
        font-size: ${
          isMobile ? theme.fontSizes.mobileCTA : theme.fontSizes.desktopCTA
        }px;
        height: ${theme.spacing(6)}px;
        margin: ${theme.spacing(0, 3)};
        width: ${theme.spacing(24)}px;
      `;
    case 'lg':
      return `
        font-size: 2rem;
        height: ${theme.spacing(9)}px;
        margin: ${theme.spacing(0, 3)};
        width: ${theme.spacing(34)}px;
      `;
    case 'xl':
      return `
        font-size: 18px;
        height: 64px;
        width: 264px;
        margin: 0;
      `;
    default:
      return `
        font-size: ${
          isMobile ? theme.fontSizes.mobileCTA : theme.fontSizes.desktopCTA
        }px;
        height: ${theme.spacing(7)}px;
        margin: ${theme.spacing(0, 4)};
        width: ${theme.spacing(34)}px;
      `;
  }
};

export const StyledCustomButton = styled(Button)<{
  boxShadow: boolean;
  textBold: boolean;
  buttonColor: string;
  variantSize: string;
  theme: ICustomTheme;
  isMobile: boolean;
}>`
  border: none;
  border-radius: ${({ theme }) => `${theme.spacing(6.5)}px`};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  line-height: ${({ theme }) => `${theme.typography.h1.lineHeight}`};
  margin-left: ${({ theme }) => `${theme.spacing(0.5)}px`};
  margin-right: ${({ theme }) => `${theme.spacing(0.5)}px`};
  min-width: ${({ theme }) => `${theme.spacing(7)}px`};
  padding-left: ${({ theme }) => `${theme.spacing(2.5)}px`};
  padding-right: ${({ theme }) => `${theme.spacing(2.5)}px`};
  text-align: center;
  text-transform: none;
  font-weight: ${(textBold) => (textBold ? 'bold' : 'normal')};
  box-shadow: ${({ boxShadow }) =>
    boxShadow ? '0 3px 6px 0 rgba(0, 0, 0, 0.16)' : 'unset'};
  background-color: ${({ buttonColor }) => buttonColor};
  color: ${({ theme }) => theme.colors.white};
  &:hover {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ buttonColor }) => buttonColor};
    border: ${({ buttonColor }) => `2px solid ${buttonColor}`};
  }
  ${({ variantSize, theme, isMobile }) =>
    setButtonSize(variantSize, theme, isMobile)};
  svg {
    margin-right: ${({ theme }) => `${theme.spacing(0.5)}px`};
  }
`;
